import { useState } from 'react'
import React from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../services/supabase'
import { Bell, Settings, Menu, MessageCircle, Star } from 'lucide-react'
import SupportModal from './SupportModal'

interface Props {
  session: Session
  children: React.ReactNode
  activePath?: string
}

interface NavItem {
  label: string
  icon: React.ReactNode
  href: string
}

function BetaBadge() {
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.08em',
        color: '#ff6b35',
        border: '1px solid #ff6b35',
        borderRadius: '4px',
        padding: '1px 5px',
        textShadow: '0 0 6px #ff6b35, 0 0 12px #ff6b35aa',
        boxShadow: '0 0 6px #ff6b35aa, inset 0 0 4px #ff6b3522',
        lineHeight: 1.4,
      }}
    >
      BETA
    </span>
  )
}

const IS_STAGING =
  import.meta.env.VITE_ENV === 'staging' ||
  window.location.hostname.includes('staging')

const SHOW_GAZZETTA = IS_STAGING || import.meta.env.DEV

const NAV: NavItem[] = [
  { label: 'Avvisi inPA', icon: <Bell size={16} />, href: '/app/alerts' },
  { label: 'Preferiti', icon: <Star size={16} />, href: '/app/saved' },
  { label: 'Impostazioni', icon: <Settings size={16} />, href: '/app/settings' },
]

// kept outside component — no session needed
function SupportButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-gray-600 hover:bg-gray-100 hover:text-gray-900 w-full"
    >
      <span><MessageCircle size={16} /></span>
      <span>Supporto</span>
    </button>
  )
}

async function handleSignOut() {
  await supabase.auth.signOut()
  window.location.href = '/'
}

function NavLinks({ onNavigate, activePath }: { onNavigate?: () => void; activePath?: string }) {
  const path = activePath ?? window.location.pathname
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.map((item, index) => {
        const active = path === item.href
        return (
          <React.Fragment key={item.href}>
            <a
              href={item.href}
              onClick={onNavigate}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                ${active
                  ? 'bg-sky-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </a>
            {index === 0 && SHOW_GAZZETTA && (
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium cursor-not-allowed select-none" style={{ color: '#c2410c', opacity: 0.45 }}>
                <span><Bell size={16} /></span>
                <span>Avvisi Gazzetta</span>
                <span
                  style={{
                    fontSize: '9px',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    color: '#c2410c',
                    border: '1px solid #c2410c',
                    borderRadius: '4px',
                    padding: '1px 4px',
                    lineHeight: 1.4,
                    whiteSpace: 'nowrap',
                  }}
                >
                  PRESTO
                </span>
              </div>
            )}
          </React.Fragment>
        )
      })}
    </nav>
  )
}

function SidebarContent({ email, onNavigate, onSupport, activePath }: { email: string; onNavigate?: () => void; onSupport: () => void; activePath?: string }) {
  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-6 py-5 border-b border-gray-100">
        <a href="/" className="flex items-center gap-2 font-bold text-sky-600 text-base">
          <Bell size={18} />
          <span>Radar Concorsi</span>
          {IS_STAGING && <BetaBadge />}
        </a>
      </div>

      {/* Nav */}
      <div className="flex-1 py-4">
        <NavLinks onNavigate={onNavigate} activePath={activePath} />
        <div className="px-3 mt-1">
          <SupportButton onClick={() => { onNavigate?.(); onSupport() }} />
        </div>
      </div>

      {/* User + signout */}
      <div className="px-4 py-4 border-t border-gray-100">
        <p className="text-xs text-gray-400 truncate mb-2 px-2">{email}</p>
        <button
          onClick={handleSignOut}
          className="w-full text-left text-sm text-gray-500 hover:text-red-600 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors"
        >
          Esci
        </button>
        <div className="flex gap-3 mt-2 px-2">
          <a href="/privacy" className="text-xs text-gray-400 hover:text-gray-600 transition-colors">Privacy</a>
          <a href="/termini" className="text-xs text-gray-400 hover:text-gray-600 transition-colors">Termini</a>
        </div>
      </div>
    </div>
  )
}

export default function AppShell({ session, children, activePath }: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [supportOpen, setSupportOpen] = useState(false)
  const email = session.user.email ?? ''

  return (
    <div className="flex bg-gray-50" style={{ minHeight: '100dvh' }}>

      {/* ── Desktop sidebar ── */}
      <aside className="hidden md:flex flex-col w-60 shrink-0 bg-white border-r border-gray-100 fixed top-0 left-0 z-30" style={{ height: '100dvh' }}>
        <SidebarContent email={email} onSupport={() => setSupportOpen(true)} activePath={activePath} />
      </aside>

      {/* ── Mobile drawer overlay ── */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* ── Mobile drawer ── */}
      <aside
        style={{ height: '100dvh' }}
        className={`fixed top-0 left-0 w-60 bg-white z-50 md:hidden transform transition-transform duration-200
          ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <SidebarContent email={email} onNavigate={() => setDrawerOpen(false)} onSupport={() => setSupportOpen(true)} activePath={activePath} />
      </aside>

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col md:ml-60" style={{ minHeight: '100dvh' }}>

        {/* Mobile top bar */}
        <header className="md:hidden flex items-center h-14 px-4 bg-white border-b border-gray-100 sticky top-0 z-20">
          <button
            onClick={() => setDrawerOpen(true)}
            className="text-gray-600 mr-3"
            aria-label="Apri menu"
          >
            <Menu size={22} />
          </button>
          <span className="font-bold text-sky-600 flex items-center gap-1.5">
            <Bell size={18} />
            <span>Radar Concorsi</span>
            {IS_STAGING && <BetaBadge />}
          </span>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>

      </div>

      {/* ── Support modal ── */}
      {supportOpen && (
        <SupportModal session={session} onClose={() => setSupportOpen(false)} />
      )}
    </div>
  )
}
